<script setup lang="ts">
import { computed } from 'vue'

import SancareCheckbox from '../atoms/SancareCheckbox.vue'

export interface SancareCheckboxGroupItem {
  key: string
  label: string
}

export interface SancareCheckboxGroupProps {
  title: string
  items: SancareCheckboxGroupItem[]
}

const props = defineProps<SancareCheckboxGroupProps>()

const model = defineModel<string[]>({ default: () => [] })

const allChecked = computed(() =>
  props.items.length > 0 && props.items.every((item) => model.value.includes(item.key))
)

function toggle(key: string): void {
  if (model.value.includes(key)) {
    model.value = model.value.filter((k) => k !== key)
  } else {
    model.value = [...model.value, key]
  }
}

function toggleAll(): void {
  if (allChecked.value) {
    model.value = model.value.filter((k) => !props.items.some((item) => item.key === k))
  } else {
    const toAdd = props.items.map((item) => item.key).filter((k) => !model.value.includes(k))
    model.value = [...model.value, ...toAdd]
  }
}

function handleItemClick(event: MouseEvent, key: string): void {
  const target = event.target as HTMLElement
  // Les clics sur input/label sont gérés par @update:model-value
  if (target.closest('label') || target.closest('input[type="checkbox"]')) {
    return
  }
  toggle(key)
}
</script>

<template>
  <div class="sancare-checkbox-group">
    <span
      class="h5 d-inline-block sancare-checkbox-group__title"
      role="button"
      tabindex="0"
      @click="toggleAll"
      @keydown.enter.space.prevent="toggleAll"
    >
      {{ title }}
    </span>
    <div
      v-for="item in items"
      :key="item.key"
      class="sancare-checkbox-group__item px-2 my-1 cp"
      :class="{ 'sancare-checkbox-group__item--checked': model.includes(item.key) }"
      @click="handleItemClick($event, item.key)"
    >
      <Sancare-checkbox
        :label="item.label"
        :model-value="model.includes(item.key)"
        @update:model-value="toggle(item.key)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';

$item-checked-bg: #edf1ff;

.sancare-checkbox-group {
  &__title {
    cursor: pointer;
  }

  &__item {
    background-color: $bg-primary-subtle;
    border: 1px solid transparent;
    border-radius: $border-radius-xs;

    &:hover,
    &:focus-within {
      background: $item-checked-bg;
    }

    &--checked {
      background-color: $item-checked-bg;
      border: 1px solid $sancare-light-blue;
    }
  }
}
</style>
